import { objectLookup } from "./objects";
import {
  isDate,
  isMap,
  isNumber,
  isString,
  isRecord,
  isSet,
  isArray,
  isPrimitive,
  isNullOrUndefined,
  type Daypart,
  DayOfTheWeek,
  FrequencyCapCommonShape
} from "../types";
import {
  daypartsToObject,
  invertDayparts,
  invertDaypartHours
} from "../components/Dayparter/utils";

/**
 * Converts a value to a readonly string representation.
 * Handles various types including null, undefined, strings, numbers, arrays, sets, maps, and records.
 * For arrays and sets, it can optionally extract values using a specified key.
 * For objects (records), it returns a JSON string representation.
 */
export function toReadonly(
  value: unknown,
  {
    defaultValue = "--",
    arrayKey
  }: { defaultValue?: string; arrayKey?: string } = {}
): string {
  if (
    isNullOrUndefined(value) ||
    isString.empty(value) ||
    isArray.empty(value) ||
    isSet.empty(value) ||
    isMap.empty(value) ||
    isRecord.empty(value)
  ) {
    return defaultValue;
  }

  if (isDate(value) || isNumber(value)) {
    return value.toLocaleString();
  }

  if (isPrimitive(value)) {
    return String(value);
  }

  if (isArray(value) || isSet(value)) {
    let val = isSet(value) ? Array.from(value) : value;
    if (arrayKey) {
      val = val.map(item => objectLookup(item, arrayKey));
    }
    if (isArray.of(val, isPrimitive)) {
      return val.join(", ");
    }
    return JSON.stringify(val);
  }

  if (isMap(value)) {
    return JSON.stringify(Object.fromEntries(value));
  }

  if (isRecord(value)) {
    return JSON.stringify(value);
  }

  return defaultValue;
}
/**
 * Formats an array of hours into human-readable daypart hours, e.g., "9am-12pm".
 */
toReadonly.daypartHours = function toDaypartHours(
  hours: number[] | undefined,
  { invertedHours = false } = {}
): string | undefined {
  const actualHours = invertedHours ? invertDaypartHours(hours) : (hours ?? []);
  if (actualHours.length === 24) {
    return "All Day";
  }
  if (actualHours.length === 0) {
    return undefined;
  }
  const hourRanges: string[] = [];
  for (let i = 0; i < actualHours.length; i++) {
    const hour = actualHours[i];

    const nextHour = actualHours[i + 1];
    const prevHour = actualHours[i - 1];
    const lastRange = hourRanges[hourRanges.length - 1];
    const inLastRange =
      lastRange?.endsWith("-") &&
      prevHour !== undefined &&
      hour === prevHour + 1;
    if (nextHour !== undefined && nextHour === hour + 1) {
      if (!inLastRange) {
        hourRanges.push(`${hour}-`);
      }
      continue;
    }
    if (inLastRange) {
      hourRanges[hourRanges.length - 1] += `${hour}`;
    } else {
      hourRanges.push(`${hour}`);
    }
  }
  return hourRanges
    .map(range => {
      const [start, end] = range.split("-");
      if (end !== undefined) {
        return (
          hourToTimeString(parseInt(start)) +
          "-" +
          hourToTimeString(parseInt(end.replace(":59", "")), {
            lastMinute: true
          })
        );
      }
      return hourToTimeString(parseInt(start));
    })
    .join(", ");
};
const DAYS_OF_WEEK = [
  DayOfTheWeek.SUNDAY,
  DayOfTheWeek.MONDAY,
  DayOfTheWeek.TUESDAY,
  DayOfTheWeek.WEDNESDAY,
  DayOfTheWeek.THURSDAY,
  DayOfTheWeek.FRIDAY,
  DayOfTheWeek.SATURDAY
];

/**
 * Formats daypart hours for each day of the week into a human-readable string.
 * If invertedHours is true, it will display the hours that are NOT included in the dayparts.
 * @returns A formatted string representing the daypart hours for each day of the week.
 */
toReadonly.dayparts = function (
  value: Daypart[] | { [dayOfWeek in string]: number[] },
  {
    invertedHours = false
  }: {
    /**
     * Whether to invert the hours (show hours not included).
     */
    invertedHours?: boolean;
  } = {}
): string {
  let res = "";
  let val = isArray(value) ? daypartsToObject(value) : value;
  if (invertedHours) {
    val = invertDayparts(val);
  }
  for (const day of DAYS_OF_WEEK) {
    const hours = val[day] || [];
    const renderedHours = toReadonly.daypartHours(hours);
    if (!renderedHours) {
      continue;
    }
    res += `${day}: ${renderedHours}\n`;
  }
  return res;
};

/**
 * Converts a 24-hour format hour to a 12-hour format time string with am/pm.
 */
function hourToTimeString(hour: number, { lastMinute = false } = {}): string {
  const ampm = hour !== 24 && hour >= 12 ? "pm" : "am";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}${lastMinute ? ":59" : ""}${ampm}`;
}

/**
 * Formats an array of frequency cap objects into a human-readable string.
 */
toReadonly.frequencyCaps = function (value: FrequencyCapCommonShape[]) {
  if (!isArray.notEmpty(value)) {
    return "";
  }
  return value
    .map(cap => {
      const timeUnit = cap.duration.time_unit;
      const max = cap.max;
      return `${max} per ${timeUnit.toLowerCase()}`;
    })
    .join("\n");
};
